import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrainingManagementPanel = ({ filters }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const trainingPrograms = [];

  const categories = [
    { id: 'all', label: 'All Programs', icon: 'BookOpen', count: 0 },
    { id: 'compliance', label: 'Compliance Training', icon: 'Shield', count: 0 },
    { id: 'skill_development', label: 'Skill Development', icon: 'TrendingUp', count: 0 },
    { id: 'leadership', label: 'Leadership', icon: 'Users', count: 0 }
  ];

  const filteredPrograms = selectedCategory === 'all'
    ? trainingPrograms
    : trainingPrograms?.filter(p => p?.category === selectedCategory);

  const getTypeBadge = (type) => {
    const badges = {
      Mandatory: 'bg-red-100 text-red-700',
      Optional: 'bg-blue-100 text-blue-700',
      Selective: 'bg-purple-100 text-purple-700'
    };
    return badges?.[type] || badges?.Optional;
  };

  const getCompetencyColor = (level) => {
    const colors = {
      Foundation: 'text-emerald-600',
      Advanced: 'text-blue-600',
      Expert: 'text-purple-600'
    };
    return colors?.[level] || colors?.Foundation;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Icon name="GraduationCap" size={28} className="text-emerald-600" />
              Training Management
            </h2>
            <p className="text-slate-600 mt-1">Comprehensive learning programs with completion tracking and certification</p>
          </div>
          <Button variant="primary" iconName="Plus">
            Create Training Program
          </Button>
        </div>

        {/* Training Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-900">Total Programs</span>
              <Icon name="BookOpen" size={18} className="text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-emerald-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">Active Enrollments</span>
              <Icon name="Users" size={18} className="text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-900">Certificates Issued</span>
              <Icon name="Award" size={18} className="text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-amber-900">Avg Completion Rate</span>
              <Icon name="TrendingUp" size={18} className="text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-amber-900">0%</p>
          </div>
        </div>
      </div>
      {/* Category Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-wrap gap-3">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                selectedCategory === category?.id
                  ? 'bg-gradient-to-r from-emerald-500 to-blue-500 text-white shadow-md'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Icon name={category?.icon} size={18} />
              <span>{category?.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                selectedCategory === category?.id ? 'bg-white/20' : 'bg-slate-200'
              }`}>
                {category?.count}
              </span>
            </button>
          ))}
        </div>
      </div>
      {/* Training Programs Grid */}
      {filteredPrograms?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="GraduationCap" size={40} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Training Programs Yet</h3>
            <p className="text-slate-600 mb-6">Create your first training program to start developing your team's skills</p>
            <Button variant="primary" iconName="Plus">
              Create Training Program
            </Button>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredPrograms?.map((program) => (
          <div key={program?.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-slate-900">{program?.title}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getTypeBadge(program?.type)}`}>
                    {program?.type}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-600">
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    <span>{program?.duration}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="User" size={14} />
                    <span>{program?.instructor}</span>
                  </div>
                </div>
              </div>
              {program?.certificateAvailable && (
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Icon name="Award" size={20} className="text-amber-600" />
                </div>
              )}
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Completion Progress</span>
                <span className="text-sm font-bold text-slate-900">{program?.completionRate}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all"
                  style={{ width: `${program?.completionRate}%` }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600 mb-1">Enrolled</p>
                <p className="text-lg font-bold text-slate-900">{program?.enrolled}</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <p className="text-xs text-emerald-700 mb-1">Completed</p>
                <p className="text-lg font-bold text-emerald-900">{program?.completed}</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700 mb-1">In Progress</p>
                <p className="text-lg font-bold text-blue-900">{program?.enrolled - program?.completed}</p>
              </div>
            </div>

            {/* Competency Level & Next Session */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <Icon name="Target" size={16} className={getCompetencyColor(program?.competencyLevel)} />
                <span className={`text-sm font-semibold ${getCompetencyColor(program?.competencyLevel)}`}>
                  {program?.competencyLevel}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Icon name="Calendar" size={14} />
                <span>Next: {new Date(program.nextSession)?.toLocaleDateString()}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 mt-4">
              <Button variant="outline" iconName="Eye" size="sm" className="flex-1">
                View Details
              </Button>
              <Button variant="primary" iconName="Download" size="sm" className="flex-1">
                Export Report
              </Button>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default TrainingManagementPanel;